/**
 * gpu-throughput.js — `window.GpuThroughput`: how much shading work this GPU
 * finishes per millisecond, MEASURED, in the units /gpu-benchmark reports.
 *
 * Why this module exists (report 2026-10-27, /ru/phone-benchmark)
 * ---------------------------------------------------------------
 * /phone-benchmark ranked phone GPUs by matching the renderer NAME against a
 * regex table, and the table was wrong in the way every such table ends up
 * wrong. Its Adreno patterns expected digits right after the word
 * ("ADRENO\s*7…"), while Chrome reports "Adreno (TM) 740" — so EVERY
 * Snapdragon, the 8 Elite (Adreno 830) included, fell through to "Entry 10/30"
 * and was told to "look at a phone with a top-tier Adreno". Meanwhile a budget
 * Mali-G57 MC2 parsed as "Mid 18" and a PowerVR GE8320 as "Unknown 14": the
 * ranking was inverted. On prod 1580 of 3009 phone runs (52%) were "Entry".
 * Fixing the regex would only reset the clock until the next chip family
 * (Immortalis, Xclipse, Maleoon, Adreno 8xx all postdate the table).
 *
 * A name is not a measurement. This module times the silicon instead, with the
 * exact workload /gpu-benchmark has run since 2026-07-22 — the same shader, the
 * same 1024×576 off-screen target, the same 40 iterations, the same forced
 * completion through a 1-pixel readPixels — so the number lands in the units of
 * the 1272 phone runs that tool has already collected with the renderer name
 * attached (2026-08-01 … 2026-10-27, both shards). Measured there, per GPU,
 * the probe is tight and ordered the way the hardware is (median units/ms,
 * IQR in brackets):
 *
 *   PowerVR GE8320  155k [152k–156k]     Adreno 642L   1.07M [1.05M–1.23M]
 *   Mali-G52 MC2    274k [256k–318k]     Mali-G615 MC6 1.56M [1.29M–2.25M]
 *   Mali-G57 MC2    440k [400k–465k]     Adreno 750    2.75M [2.68M–2.82M]
 *   Adreno 610      593k [545k–636k]     Adreno 830    2.68M [2.04M–3.65M]
 *
 * which is why the workload lives HERE and /gpu-benchmark reads it from here:
 * two copies of a shader drift apart silently, and then the deep test and the
 * summary disagree about the same phone.
 *
 * What it does NOT know: the model. Callers show the renderer string as the
 * browser reported it (iOS says "Apple GPU" for every chip) and score the
 * throughput — so a GPU nobody has heard of yet still lands where it belongs.
 *
 * Contract
 *   GpuThroughput.workload — the shared constants and shader sources
 *   GpuThroughput.rampDone(tps, lastDt) — has a warming GPU stopped speeding up?
 *       (/gpu-benchmark warms its first phase by the same rule)
 *   GpuThroughput.measure({budgetMs, maxWallMs}) → Promise<{
 *       ok, tp,            // units (pixel-iterations) per ms of GPU time
 *       gpuMs, batches, wallMs,
 *       warmupMs,          // how long the GPU took to stop speeding up (see rampDone)
 *       reason             // when !ok: 'no_webgl2'|'shader'|'hidden'|'context_lost'|'timeout'|'error'
 *   }>
 *
 * Main-thread cost: the GPU time itself (≈2.2 s — 1.5–2.5 s of warm-up plus
 * 0.65 s measured; ~3 s on a Mali-T720-class chip, where one batch alone is
 * ~1 s), cut into ≤ 20 ms slices per animation frame. NEVER throws — every failure resolves {ok:false}.
 *
 * Written ES2018-safe (var/function, no ?./??): it runs on the oldest Android
 * WebViews we still see, and a parse error would take the caller with it.
 */
(function () {
    'use strict';

    if (window.GpuThroughput) return; // duplicate <script> guard

    var W = {
        MEAS_W: 1024,               /* off-screen resolution, device-independent */
        MEAS_H: 576,
        SHADER_ITERS: 40,           /* heavy-shader inner-loop iterations */
        PHASE_MEASURE_MS: 650,      /* real GPU time to accumulate */
        TICK_BUDGET_MS: 20,         /* block at most ~this per animation frame */
        WARMUP_MS: 120,             /* discarded first, so the GPU reaches its boost
                                       clock and shaders are warm (laptop GPUs ramp
                                       slowly — 2 cold batches read ~2× low) */
        PROBE_WARMUP_MS: 1500,      /* measure() only: the load /gpu-benchmark has put on
                                       the GPU (particles + geometry, 2 × 770 ms of GPU
                                       time) by the time it times its shader phase — the
                                       conditions the phone calibration was taken in */
        WARMUP_MAX_MS: 2500,        /* …and past that, until the throughput stops
                                       rising, up to this */
        HEAVY_VS:
            '#version 300 es\n' +
            'precision highp float;\n' +
            'layout(location=0) in vec2 aPos;\n' +
            'out vec2 vUV;\n' +
            'void main(){\n' +
            '    vUV = aPos * 0.5 + 0.5;\n' +
            '    gl_Position = vec4(aPos, 0.0, 1.0);\n' +
            '}',
        HEAVY_FS:
            '#version 300 es\n' +
            'precision highp float;\n' +
            'in vec2 vUV;\n' +
            'out vec4 fragColor;\n' +
            'uniform float uTime;\n' +
            'uniform int uIters;\n' +
            'void main(){\n' +
            '    vec2 uv = vUV;\n' +
            '    vec3 col = vec3(0.0);\n' +
            '    vec2 p = uv;\n' +
            '    for(int i = 0; i < 512; i++){\n' +
            '        if(i >= uIters) break;\n' +
            '        float fi = float(i);\n' +
            '        p = uv * (1.5 + fi * 0.15) + vec2(sin(uTime * 0.7 + fi), cos(uTime * 0.5 + fi * 1.3));\n' +
            '        float v = sin(p.x * 3.14159 + uTime) * cos(p.y * 3.14159 - uTime * 0.8);\n' +
            '        v += sin(length(p) * 6.0 - uTime * 2.0) * 0.5;\n' +
            '        col += 0.5 + 0.5 * cos(6.283 * (sin(v * 3.14159) + fi * 0.1 + vec3(0.0, 0.33, 0.67)));\n' +
            '    }\n' +
            '    fragColor = vec4(col / float(max(uIters, 1)), 1.0);\n' +
            '}'
    };

    /* Units of one batch: every pixel of the target runs the loop SHADER_ITERS
       times. /gpu-benchmark's batchUnits('shaders') is this same product. */
    function batchUnits() { return W.MEAS_W * W.MEAS_H * W.SHADER_ITERS; }

    function compile(gl, type, src) {
        var s = gl.createShader(type);
        gl.shaderSource(s, src);
        gl.compileShader(s);
        if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) { gl.deleteShader(s); return null; }
        return s;
    }

    /* Is the GPU done ramping its clock? /gpu-benchmark times shaders as its
       THIRD phase, after ~1.5 s of particles and geometry, so the phone data
       this module is calibrated on was taken on a warm GPU. A cold one is
       slower for an unpredictable while — measured 2026-10-28 on an idle
       RTX 4060 laptop, median per 250 ms window, three cold starts:
         2.9M → 26M → 39M (ramped by 500 ms)
         2.5M … 2.5M → 26M → 39M (ramped by 2 s)
         3.9M … 3.1M (still cold at 3 s)
       WARMUP_MS alone (120 ms) measured them at 2–20M of 39M. "Stop when the
       throughput stops rising" alone is not enough either: a cold plateau is
       flat too, and six cold starts stopped on it at 2.5–4.3M three times.
       So the probe first reproduces the reference's conditions —
       PROBE_WARMUP_MS of continuous load, what /gpu-benchmark has run before
       its shader phase — and then also waits until the last window is no
       faster than the one before it (within 5 %), bounded by WARMUP_MAX_MS.
       A GPU still cold after that is measured as it is: that is what it does
       under load right now. With this, six cold starts on that laptop gave
       35.7 / 42.7 / 42.5 / 39.4M and twice 8.0 / 3.9M — its discrete GPU
       sometimes stays cold for over 2.5 s of full load, a hybrid-laptop
       power state. Phones evidently do not: the reference runs, taken at the
       same point, are tight per model (Adreno 750 IQR 2.68–2.82M), which a
       multi-second cold plateau would have smeared. A window is ≥ 100 ms of
       batches; a batch that alone takes ≥ 200 ms is its own window (the
       clock ramps inside it). */
    function rampDone(tps, lastDt) {
        var k = lastDt >= 200 ? 1 : Math.max(2, Math.min(20, Math.ceil(100 / Math.max(lastDt, 1))));
        if (tps.length < 2 * k) return false;
        var med = function (a) { var s = a.slice().sort(function (x, y) { return x - y; }); return s[s.length >> 1]; };
        var recent = med(tps.slice(-k)), before = med(tps.slice(-2 * k, -k));
        return recent <= before * 1.05;
    }

    function measure(opts) {
        opts = opts || {};
        var budgetMs = opts.budgetMs > 0 ? opts.budgetMs : W.PHASE_MEASURE_MS;
        /* A wall-clock ceiling only for a GPU that never comes back: the slowest
           real phone (Mali-T720, 25k units/ms) needs ~2.6 s of GPU time in
           total, so 15 s is never the reason a working device stops. */
        var maxWallMs = opts.maxWallMs > 0 ? opts.maxWallMs : 15000;

        return new Promise(function (resolve) {
            var gl = null, canvas = null, settled = false, lost = false, raf = 0;
            var accumMs = 0, accumUnits = 0, batches = 0, warmupMs = 0, warm = false, warmTps = [];
            var t0 = performance.now();

            /* A hidden page gets no frames (so `tick` would simply never run
               again) and a throttled GPU — what it would measure is the
               browser's background policy, not the chip. */
            function onVis() { if (document.visibilityState === 'hidden') fail('hidden'); }
            document.addEventListener('visibilitychange', onVis);

            function finish(res) {
                if (settled) return;
                settled = true;
                document.removeEventListener('visibilitychange', onVis);
                if (raf) { try { cancelAnimationFrame(raf); } catch (_) {} raf = 0; }
                try {
                    var lose = gl && gl.getExtension('WEBGL_lose_context');
                    if (lose) lose.loseContext();
                } catch (_) {}
                res.wallMs = Math.round(performance.now() - t0);
                res.warmupMs = Math.round(warmupMs);
                res.gpuMs = Math.round(accumMs);
                res.batches = batches;
                resolve(res);
            }
            function fail(reason) { finish({ ok: false, tp: 0, reason: reason }); }

            try {
                canvas = document.createElement('canvas');
                canvas.width = 1; canvas.height = 1;
                canvas.addEventListener('webglcontextlost', function (e) {
                    try { e.preventDefault(); } catch (_) {}
                    lost = true;
                });
                gl = canvas.getContext('webgl2', {
                    alpha: false, antialias: false, depth: false, stencil: false,
                    powerPreference: 'high-performance', preserveDrawingBuffer: false
                });
                if (!gl) { fail('no_webgl2'); return; }

                var vs = compile(gl, gl.VERTEX_SHADER, W.HEAVY_VS);
                var fs = compile(gl, gl.FRAGMENT_SHADER, W.HEAVY_FS);
                if (!vs || !fs) { fail('shader'); return; }
                var prog = gl.createProgram();
                gl.attachShader(prog, vs); gl.attachShader(prog, fs);
                gl.linkProgram(prog);
                if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) { fail('shader'); return; }
                var uTime = gl.getUniformLocation(prog, 'uTime');
                var uIters = gl.getUniformLocation(prog, 'uIters');

                var vao = gl.createVertexArray();
                gl.bindVertexArray(vao);
                var buf = gl.createBuffer();
                gl.bindBuffer(gl.ARRAY_BUFFER, buf);
                gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
                gl.enableVertexAttribArray(0);
                gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

                /* The target /gpu-benchmark measures into: colour texture plus a
                   depth renderbuffer, both cleared on every batch. The depth
                   clear is part of what a batch costs there, so it is here. */
                var tex = gl.createTexture();
                gl.bindTexture(gl.TEXTURE_2D, tex);
                gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, W.MEAS_W, W.MEAS_H, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
                gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
                gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
                var rb = gl.createRenderbuffer();
                gl.bindRenderbuffer(gl.RENDERBUFFER, rb);
                gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_COMPONENT16, W.MEAS_W, W.MEAS_H);
                var fbo = gl.createFramebuffer();
                gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
                gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
                gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.RENDERBUFFER, rb);
                if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) { fail('error'); return; }
                gl.viewport(0, 0, W.MEAS_W, W.MEAS_H);
                gl.disable(gl.DEPTH_TEST);
                gl.useProgram(prog);
                gl.uniform1i(uIters, W.SHADER_ITERS);
                gl.clearColor(0, 0, 0, 1);

                var px = new Uint8Array(4);
                var units = batchUnits();

                var tick = function () {
                    raf = 0;
                    if (settled) return;
                    if (lost || (gl.isContextLost && gl.isContextLost())) { fail('context_lost'); return; }
                    if (document.visibilityState === 'hidden') { fail('hidden'); return; }
                    var spent = 0;
                    do {
                        var b0 = performance.now();
                        gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
                        gl.uniform1f(uTime, b0 * 0.001);
                        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
                        gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, px); /* forces completion */
                        var dt = performance.now() - b0;
                        spent += dt;
                        if (!warm) {
                            warmupMs += dt;
                            if (dt > 0) warmTps.push(units / dt);
                            warm = warmupMs >= W.PROBE_WARMUP_MS && (warmupMs >= W.WARMUP_MAX_MS || rampDone(warmTps, dt));
                        } else if (dt > 0) {
                            accumMs += dt;
                            accumUnits += units;
                            batches++;
                        }
                    } while (spent < W.TICK_BUDGET_MS && accumMs < budgetMs);

                    if (accumMs >= budgetMs) {
                        finish({ ok: true, tp: Math.round(accumUnits / accumMs) });
                        return;
                    }
                    if (performance.now() - t0 > maxWallMs) {
                        /* Two measured batches are already a throughput; fewer is not. */
                        if (batches >= 2) finish({ ok: true, tp: Math.round(accumUnits / accumMs), partial: true });
                        else fail('timeout');
                        return;
                    }
                    raf = requestAnimationFrame(tick);
                };
                raf = requestAnimationFrame(tick);
            } catch (e) {
                fail('error');
            }
        });
    }

    window.GpuThroughput = {
        workload: W,
        batchUnits: batchUnits,
        rampDone: rampDone,
        measure: measure
    };
})();
